import { useAppSelector } from '../../../app/hooks';
import { selectQueriedAssets } from '../../../data/assets/queriedAssetsSlice';
import { getAssetInfo } from '../../../data/assets/helpers';
import { APP_NAME } from '../../../app/constants';

/**
 * Utility to clean strings for filenames
 * Could be replaced by a call to a library like slugify if desired
 */
export const slugify = (text: string, maxLen = 30): string => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s/\\|]+/g, '_') // Spaces/slashes to underscores
    .replace(/[^a-z0-9\-_]/g, '') // Remove non-word chars
    .replace(/_{2,}/g, '_') // Collapse multiple underscores
    .substring(0, maxLen)
    .replace(/^_+|_+$/g, ''); // Trim underscores from ends
};

export interface TpExportFileNameInfo {
  assetId: string;
  dataType: string;
  title: string;
}

/**
 * General hook to generate export filenames for charts or other dashboard components
 * Contains the app name, Asset name, component title, and current date
 * Parts are truncated and slugified to ensure valid filenames
 * Max filename length is expected to be around 9 + 30 + 30 + 10 = 79 characters plus extension
 * @returns string filename
 */
export const useExportFilename = () => {
  const queriedAssetsData = useAppSelector(selectQueriedAssets);

  const getFilename = ({ assetId, title, dataType }: TpExportFileNameInfo, extension = 'png') => {
    const assetInfo = getAssetInfo(assetId, queriedAssetsData);
    const assetName = assetInfo?.displayName ?? '';

    // Current date in YYYY-MM-DD format
    const downloadDate = new Date().toISOString().split('T')[0];

    // Build components in order of importance
    const parts = [APP_NAME, assetName, title || dataType, downloadDate];

    // Slugify, filter out empty parts and join
    const baseName = parts
      .map((part) => slugify(part))
      .filter(Boolean)
      .join('_');
    return `${baseName}.${extension}`;
  };

  return { getFilename };
};
